import React, { useState, useLayoutEffect, } from 'react';
import { getContext, useCombinedRefs, getEasing, getDuration, isAnimationDisabled } from 'common';
import _debounce from 'lodash/debounce';
import _merge from 'lodash/merge';
import { useSpring } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import IconButton from 'components/IconButton';
import ChevronLeftIcon from '@nokia-csf-uxr/ccfk-assets/ChevronLeftIcon';
import ChevronRightIcon from '@nokia-csf-uxr/ccfk-assets/ChevronRightIcon';
import 'scroll-behavior-polyfill';/* for IE11/EDGE support of element.scroll */
import Styled from './styles';
import Tab from './Tab';
import handleKeyDown from './util/keyboardNav';

const SCROLL_BUTTON_WIDTH = 50;

interface TabsContextInterface {
  rtl?: boolean;
  variant?: string;
  selectedRef?: any;
  setSelectedRef?: (any) => void;
  setSelectedDims?: (any) => void;
  setFocusedRef?: (any) => void;
  vertical?: boolean,
  disableAnimation?: boolean,
  theme?: any
}

const TabsContext = React.createContext<TabsContextInterface | null>({});

export interface TabsProps extends React.ComponentPropsWithRef<'div'> {
  /** Align to the left or center (use center for smaller container). */
  alignment?: 'left' | 'center' | 'right';
  /** Set to true, each tab will take as much width as possible within the tab container. This prop has no affect on icon tabs. */
  fullWidth?: boolean;
  /** Enables scrollable tabs (enabled by default) */
  scroll?: boolean;
  /** Right-to-left. It's preferred that RTL is toggled at an application level. However if you need a component level override, this prop can help. */
  rtl?: boolean;
  /** Render prop to render custom selected-indicator. { selectedRef, innerRef, containerRef } */
  renderSelectedIndicator?: (arg:{
    selectedRef?: any,
    innerRef?: any,
    containerRef?: any
  }) => JSX.Element;
  /** Accepts multiple <Tab /> children. */
  children: React.ReactNode;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To override the left button. */
  renderLeftButton?: (arg:{
    isScrollable?: any,
    selectedRef?: any,
    innerRef?: any,
    containerRef?: any
  }) => JSX.Element;
  /** To override the right button. */
  renderRightButton?: (arg:{
    isScrollable?: any,
    selectedRef?: any,
    innerRef?: any,
    containerRef?: any
  }) => JSX.Element;
  /** @ignore specify variant of tabs */
  variant?: string;
}

/** Use Tabs to navigate between different views.
 * Any other props than explicitly defined will be provided to the wrapper element.
 * To get the ref of the wrapper with `TabsInner` class name, use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
*/
const Tabs = React.forwardRef<HTMLElement, TabsProps>((props: TabsProps, ref) =>
  getContext(({
    RTL = false,
    disableAnimation,
    theme: {
      HORIZONTAL_NAVIGATION_TAB: tabTheme,
      ICON_BUTTON: iconButtonTheme,
    }
  }) => {
    const {
      alignment,
      fullWidth,
      scroll,
      rtl,
      renderSelectedIndicator,
      children,
      theme: userTheme,
      disableAnimation: propsdisableAnimation,
      variant,
      ...otherProps
    } = props;
    const [selectedRef, setSelectedRef] = useState<any>();
    const [selectedDims, setSelectedDims] = useState<any>();
    const [isScrollable, setIsScrollable] = useState<{
      canScrollLeft?: boolean,
      canScrollRight?: boolean,
    } | false>(false);
    const [, setX] = useSpring(() => ({ x: 0 }));
    const innerRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const leftButtonRef = React.useRef(null);
    const rightButtonRef = React.useRef(null);
    const selectedIndicator = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const isRTL = rtl || RTL;
    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const updateSelectedIndicator = _debounce(() => {
      if (selectedRef && !renderSelectedIndicator) {
        const { offsetLeft: left, clientWidth: width } = selectedRef;
        setSelectedDims({ left, width });
      }
    }, 100);

    const updateScrollable = _debounce(() => {
      if (!combinedRef.current) return;
      const { scrollWidth, clientWidth, scrollLeft } = combinedRef.current;
      if (scroll && !props.renderLeftButton && !props.renderRightButton) {
        /** Legacy Edge Browser interprets scrollLeft differently to chrome. */
        const canScrollLeft = () => {
          if (isRTL) {
            return Math.abs(scrollLeft) !== scrollWidth - clientWidth;
          }
          return scrollLeft > 0;
        };
        const canScrollRight = () => {
          if (isRTL) {
            return scrollLeft < 0;
          }
          return Math.abs(scrollLeft) !== scrollWidth - clientWidth;
        };
        setIsScrollable({ canScrollLeft: canScrollLeft(), canScrollRight: canScrollRight() });
      }
    }, 100);

    const scrollTo = direction => () => {
      if (!combinedRef.current) return;
      const element = combinedRef && combinedRef.current;
      const scrollDistance = containerRef.current.clientWidth * 0.75;
      const totalScrollDistance =
        (direction === 'right' ? -scrollDistance : scrollDistance) - SCROLL_BUTTON_WIDTH;
      element &&
        setX({
          config: {
            duration: getDuration(SPEED_FAST),
            easing: getEasing(TRANSITION_ACCELERATE)
          },
          x: (element.scrollLeft as number) + totalScrollDistance,
          // @ts-expect-error: Review the API of setY
          onFrame: ({ x }) => {
            element.scroll(x, 0);
          }
        });
    };

    const isScrollableLeft = scroll && isScrollable && isScrollable.canScrollLeft;
    const isScrollableRight = scroll && isScrollable && isScrollable.canScrollRight;

    const scrollToTab = (tab) => {
      if (!combinedRef.current) return;
      const element = combinedRef && combinedRef.current;
      const tabElement = tab;
      if (!tabElement) return;

      const { clientWidth } = element;
      const { width: tabWidth, right: tabRight, left: tabLeft } = tabElement.getBoundingClientRect();
      const sWidth = isRTL ? clientWidth : isScrollableRight ? (clientWidth - SCROLL_BUTTON_WIDTH) : clientWidth;
      const shouldScroll = () => isRTL ? (sWidth < tabRight) || tabRight < 0 : (sWidth < tabLeft) || tabLeft < 0;
      if (!shouldScroll()) return;
      const scrollDistance = isRTL ? -containerRef.current.clientWidth : containerRef.current.clientWidth;
      const totalScrollDistance = isRTL ? (tabLeft + scrollDistance) + tabWidth : (tabRight - scrollDistance) + tabWidth;
      element &&
        setX({
          config: { duration: getDuration(SPEED_FAST), easing: getEasing(TRANSITION_ACCELERATE) },
          x: (element.scrollLeft as number) + (totalScrollDistance as number),
          // @ts-expect-error: Review the API of setY
          onFrame: ({ x }) => {
            element.scroll(x, 0);
          }
        });
    };

    // Scroll to selected tab
    const scrollToSelected = (tab) => {
      if (!tab || !scroll || !(document && document.scrollingElement)) return;
      scrollToTab(tab);

      /** TODO: this code is better, but isn't crossbrowser in RTL (Firefox and Edge interpret scrollLeft and left values differently than webkit) */
      // const { left: selectedLeft, width: selectedWidth } = ref.getBoundingClientRect();
      // const { left: containerLeft, width: containerWidth } = container.getBoundingClientRect();
      // const containerScrollLeft = getNormalizedScrollLeft(container, isRTL ? 'rtl' : 'ltr' )
      // const shouldScrollToSelected = (Math.abs(ref.offsetLeft) > container.offsetWidth - containerScrollLeft); //scrolls when selectedtab is outside of current scroll area
      // const scrollDistance =
      //   (isEdge && isRTL)
      //     ? containerScrollLeft + ((selectedLeft - selectedWidth) - containerLeft)
      //     : containerScrollLeft + ((selectedLeft - selectedWidth) - containerLeft);
      // shouldScrollToSelected &&
      //   container &&
      //   setX({
      //     // config: { mass: 1, tension: 50, friction: 50 },
      //     x: scrollDistance,
      //     onFrame: props => container.scroll(props.x, 0)
      //   });
    };

    // Set selected tab, and scroll to it if needed.
    const setSelectedRefFunc = (tab) => {
      setSelectedRef(tab);
      scrollToSelected(tab);
    };
    const setSelectedDimsFunc = (dims) => {
      if (dims) {
        const { width, left } = dims;
        setSelectedDims({ left, width });
      }
    };

    const setFocusedRefFunc = (tab) => {
      scrollToSelected(tab);
    };

    // Update selected tab indicator
    useLayoutEffect(() => {
      updateSelectedIndicator();
      window.addEventListener('resize', updateSelectedIndicator);
      return () => {
        window.removeEventListener('resize', updateSelectedIndicator);
      };
    }, [isRTL, selectedRef, isScrollable, props]);

    useLayoutEffect(() => {
      if (scroll) {
        updateScrollable();
        combinedRef.current.addEventListener('scroll', updateScrollable);
        window.addEventListener('resize', updateScrollable);
        return () => {
          combinedRef.current.removeEventListener('scroll', updateScrollable);
          window.removeEventListener('resize', updateScrollable);
        };
      }
      return undefined;
    }, [isRTL, ref, scroll]);

    const resolvedTheme = _merge({}, tabTheme, userTheme);
    const renderLeftButton = () => {
      if (isScrollableLeft) {
        return (
          <Styled.TabScrollButtonContainer left ref={leftButtonRef} theme={resolvedTheme} data-test="ccfk-tabs-left-scroll-button">
            <IconButton
              size="small"
              theme={iconButtonTheme.FLAT}
              onClick={scrollTo('right')}
            >
              {isRTL ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Styled.TabScrollButtonContainer>
        );
      }
      return props.renderLeftButton && props.renderLeftButton({
        isScrollable,
        selectedRef,
        innerRef: combinedRef,
        containerRef: combinedRef
      });
    };

    const renderRightButton = () => {
      if (isScrollableRight) {
        return (
          <Styled.TabScrollButtonContainer right ref={rightButtonRef} theme={resolvedTheme} data-test="ccfk-tabs-right-scroll-button">
            <IconButton
              size="small"
              theme={iconButtonTheme.FLAT}
              onClick={scrollTo('left')}
            >
              {isRTL ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Styled.TabScrollButtonContainer>
        );
      }
      return props.renderRightButton && props.renderRightButton({
        isScrollable,
        selectedRef,
        innerRef: combinedRef,
        containerRef: combinedRef
      });
    };

    const renderTheSelectedIndicator = () => (
      (renderSelectedIndicator ? (
        renderSelectedIndicator({ selectedRef, innerRef: combinedRef, containerRef })
      ) : (
        <Styled.TabsSelectedIndicator
          theme={resolvedTheme}
          style={{ ...selectedDims }}
          variant={variant}
          ref={selectedIndicator}
          data-test="ccfk-tabs-selected-indicator"
        />
      )));
    return (
      <Styled.Tabs
        ref={containerRef}
        data-test="ccfk-tabs-container"
        role="tablist"
        variant={variant}
        theme={resolvedTheme}
        {...otherProps}
        rtl={isRTL}
        onKeyDown={handleKeyDown(props, combinedRef)}
        alignment={alignment}
      >
        <TabsContext.Provider
          value={{
            setSelectedRef: setSelectedRefFunc,
            setSelectedDims: setSelectedDimsFunc,
            selectedRef,
            variant,
            rtl: isRTL,
            setFocusedRef: setFocusedRefFunc,
            disableAnimation: resolvedDisableAnimation,
            theme: resolvedTheme,
          }}
        >
          {renderLeftButton()}
          <Styled.TabsInner
            data-test="ccfk-tabs-inner-container"
            ref={combinedRef}
            fullWidth={fullWidth}
            scrollable={isScrollable && scroll}
          >
            {children}
            {renderTheSelectedIndicator()}
          </Styled.TabsInner>
          {renderRightButton()}
        </TabsContext.Provider>
      </Styled.Tabs>
    );
  }));

Tabs.displayName = 'Tabs';
Tabs.defaultProps = {
  renderLeftButton: undefined,
  renderRightButton: undefined,
  alignment: 'center',
  fullWidth: false,
  scroll: true,
  rtl: false,
  renderSelectedIndicator: undefined,
  disableAnimation: undefined,
  theme: {},
  variant: undefined
};

export { Tabs as default, Tab, TabsContext };
